export type DatePreset =
    | 'wtd'
    | '7days'
    | '2weeks'
    | 'thismonth'
    | 'thisquarter'
    | '1month'
    | '3months'
    | '6months'
    | '1year'
    | 'custom';

export type Granularity = 'day' | 'week' | 'biweekly' | 'month';

export interface PresetOption {
    value: DatePreset;
    label: string | (() => string);
    granularity: Granularity;
}

const getMonthName = (date: Date): string => date.toLocaleString('en-US', { month: 'long' });

const getCurrentQuarter = (date: Date): number => Math.floor(date.getMonth() / 3) + 1;

const getQuarterLabel = (date: Date): string => `Q${getCurrentQuarter(date)}`;

const getQuarterStartMonth = (quarter: number): number => (quarter - 1) * 3;

export const PRESET_OPTIONS: PresetOption[] = [
    { value: 'wtd', label: 'Week-to-date', granularity: 'day' },
    { value: '7days', label: 'Last Week', granularity: 'day' },
    { value: '2weeks', label: 'Last Two Weeks', granularity: 'day' },
    { value: 'thismonth', label: () => `This Month: ${getMonthName(new Date())}`, granularity: 'week' },
    { value: 'thisquarter', label: () => `This Quarter: ${getQuarterLabel(new Date())}`, granularity: 'month' },
    { value: '1month', label: 'Last 30 Days', granularity: 'week' },
    { value: '3months', label: 'Last 3 Months', granularity: 'week' },
    { value: '6months', label: 'Last 6 Months', granularity: 'biweekly' },
    { value: '1year', label: 'Last Year', granularity: 'month' },
];

export const getCustomGranularity = (startStr: string, endStr: string): Granularity => {
    const days =
        Math.round(
            (new Date(endStr + 'T00:00:00').getTime() - new Date(startStr + 'T00:00:00').getTime()) /
                (1000 * 60 * 60 * 24),
        ) + 1;
    if (days <= 14) return 'day';
    if (days <= 60) return 'week';
    if (days <= 180) return 'biweekly';
    return 'month';
};

export const formatShortDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const startOfLocalDay = (d: Date): Date => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

/**
 * Reporting day D = [D 08:00 UTC, D+1 08:00 UTC).
 * Must match packages/functions reporting dateRange (REPORTING_DAY_UTC_OFFSET_HOURS = -8).
 */
export const REPORTING_DAY_UTC_OFFSET_HOURS = -8;

/** YYYY-MM-DD for a UTC calendar day. */
const utcYmd = (y: number, m0: number, d: number): string => {
    const mm = String(m0 + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${y}-${mm}-${dd}`;
};

/** Local midnight Date for a YYYY-MM-DD civil date (calendar widgets / dateToIsoStr). */
export const localDateFromYmd = (ymd: string): Date => {
    const [y, m, d] = ymd.split('-').map(Number);
    return startOfLocalDay(new Date(y, m - 1, d));
};

/**
 * Reporting dateKey currently open (receiving orders) at `now`.
 * At Wed 08:00 UTC the open key becomes Wednesday; before that it is still Tuesday.
 */
export const getOpenReportingDateKey = (now: Date = new Date()): string => {
    const shifted = new Date(now.getTime() + REPORTING_DAY_UTC_OFFSET_HOURS * 60 * 60 * 1000);
    return utcYmd(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate());
};

/**
 * Latest complete reporting dateKey (rollup day closed at 08:00 UTC).
 * At Wed 08:00 UTC → Tuesday; at Wed 07:59 UTC → Monday.
 */
export const getLatestCompleteReportingDateKey = (now: Date = new Date()): string => {
    const shifted = new Date(now.getTime() + REPORTING_DAY_UTC_OFFSET_HOURS * 60 * 60 * 1000);
    const complete = new Date(
        Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate() - 1),
    );
    return utcYmd(complete.getUTCFullYear(), complete.getUTCMonth(), complete.getUTCDate());
};

export const getOpenReportingDate = (now: Date = new Date()): Date =>
    localDateFromYmd(getOpenReportingDateKey(now));

export const getLatestCompleteReportingDate = (now: Date = new Date()): Date =>
    localDateFromYmd(getLatestCompleteReportingDateKey(now));

export const getTodayStart = (): Date => startOfLocalDay(new Date());

/** @deprecated Prefer getLatestCompleteReportingDate — kept as alias for calendar max end. */
export const getYesterdayStart = (now: Date = new Date()): Date => getLatestCompleteReportingDate(now);

/**
 * End date for period-to-date presets (WTD / This Month / This Quarter).
 * Default: latest complete reporting day (closed at 08:00 UTC).
 *
 * Exceptions (keep open reporting day so start ≤ end; expect sparse until that day closes):
 * - Week-to-date when week starts on the open reporting day
 * - This Month when month starts on the open reporting day
 * - This Quarter when quarter starts on the open reporting day
 */
export const getPeriodToDateEnd = (periodStart: Date, now: Date = new Date()): Date => {
    const open = getOpenReportingDate(now);
    const start = startOfLocalDay(periodStart);
    if (start.getTime() === open.getTime()) {
        return open;
    }
    return getLatestCompleteReportingDate(now);
};

/** Monday of the week containing `day` (local). Sunday → previous Monday. */
export const getWeekToDateStart = (day: Date = getTodayStart()): Date => {
    const start = startOfLocalDay(day);
    const dow = start.getDay();
    const diff = dow === 0 ? 6 : dow - 1;
    start.setDate(start.getDate() - diff);
    return start;
};

/** Previous calendar month + month of today (left pane for custom dual-month view). */
export const getLastTwoCalendarMonthsStart = (): Date => {
    const today = getTodayStart();
    return new Date(today.getFullYear(), today.getMonth() - 1, 1);
};

/** Left calendar month = month before range end (preset preview). */
export const getViewLeftMonthForAppliedRange = (endStr: string): Date => {
    const e = startOfLocalDay(new Date(endStr + 'T00:00:00'));
    return new Date(e.getFullYear(), e.getMonth() - 1, 1);
};

/** Clamp custom ranges so they cannot extend past the latest complete reporting day. */
export const clampReportingRangeDates = (
    start: Date,
    end: Date,
    now: Date = new Date(),
): { start: Date; end: Date } => {
    const maxEnd = getLatestCompleteReportingDate(now);
    let s = startOfLocalDay(start);
    let e = startOfLocalDay(end);
    if (e.getTime() > maxEnd.getTime()) e = maxEnd;
    if (s.getTime() > maxEnd.getTime()) s = maxEnd;
    if (s.getTime() > e.getTime()) {
        const t = s;
        s = e;
        e = t;
    }
    return { start: s, end: e };
};

export const getPresetDates = (
    preset: DatePreset,
    now: Date = new Date(),
): { startDate: string; endDate: string } => {
    // Civil "today" for week/month/quarter starts follows the open reporting day (UTC 08:00 cut).
    const today = getOpenReportingDate(now);
    const complete = getLatestCompleteReportingDate(now);

    switch (preset) {
        case 'wtd': {
            const start = getWeekToDateStart(today);
            const end = getPeriodToDateEnd(start, now);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case '7days': {
            const end = complete;
            const start = new Date(complete);
            start.setDate(end.getDate() - 6);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case '2weeks': {
            const end = complete;
            const start = new Date(complete);
            start.setDate(end.getDate() - 13);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case 'thismonth': {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = getPeriodToDateEnd(start, now);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case 'thisquarter': {
            const quarterStartMonth = getQuarterStartMonth(getCurrentQuarter(today));
            const start = new Date(today.getFullYear(), quarterStartMonth, 1);
            const end = getPeriodToDateEnd(start, now);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case '1month': {
            const end = complete;
            const start = new Date(complete);
            start.setDate(end.getDate() - 29);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case '3months': {
            const end = complete;
            const start = new Date(complete);
            start.setDate(end.getDate() - 89);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case '6months': {
            const end = complete;
            const start = new Date(complete);
            start.setDate(end.getDate() - 179);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case '1year': {
            const end = complete;
            const start = new Date(complete);
            start.setDate(end.getDate() - 364);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
        case 'custom': {
            const end = complete;
            const start = new Date(complete);
            start.setDate(end.getDate() - 29);
            return { startDate: dateToIsoStr(start), endDate: dateToIsoStr(end) };
        }
    }
};

export const getGranularityForPreset = (preset: DatePreset): Granularity => {
    const option = PRESET_OPTIONS.find((o) => o.value === preset);
    if (!option) return 'week';
    return option.granularity;
};

export const mapPresetToBackend = (preset: DatePreset): string => {
    switch (preset) {
        case 'wtd':
            return 'wtd';
        case '7days':
            return 'last_7_days';
        case '2weeks':
            return 'last_14_days';
        case 'thismonth':
            return 'this_month';
        case 'thisquarter':
            return 'this_quarter';
        case '1month':
            return 'last_30_days';
        case '3months':
            return 'last_3_months';
        case '6months':
            return 'last_6_months';
        case '1year':
            return 'last_year';
        case 'custom':
            return 'custom';
        default:
            return 'last_7_days';
    }
};

export const dateToIsoStr = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
};

export const getReportingTimeframeTriggerLabel = (
    datePreset: DatePreset,
    startDate: string,
    endDate: string
): string => {
    if (datePreset === 'custom') {
        return `${formatShortDate(startDate)} – ${formatShortDate(endDate)}`;
    }
    const option = PRESET_OPTIONS.find((o) => o.value === datePreset);
    if (!option) return 'Custom Range';
    return typeof option.label === 'function' ? option.label() : option.label;
};

export interface ReportingTimeframeApplyPayload {
    preset: DatePreset;
    startDate: string;
    endDate: string;
    granularity: Granularity;
}

const REPORTING_TIMEFRAME_STORAGE_KEY = 'reporting_timeframe';

const DATE_PRESETS = new Set<string>([...PRESET_OPTIONS.map((option) => option.value), 'custom']);
const GRANULARITIES = new Set<string>(['day', 'week', 'biweekly', 'month']);
const YMD = /^\d{4}-\d{2}-\d{2}$/;

function isDatePreset(value: unknown): value is DatePreset {
    return typeof value === 'string' && DATE_PRESETS.has(value);
}

function isGranularity(value: unknown): value is Granularity {
    return typeof value === 'string' && GRANULARITIES.has(value);
}

/** Restores the last reporting range. Named presets are recomputed so "Last Week" stays current. */
export function loadReportingTimeframe(
    fallbackPreset: DatePreset = '7days',
): ReportingTimeframeApplyPayload {
    const fallback = (): ReportingTimeframeApplyPayload => ({
        preset: fallbackPreset,
        ...getPresetDates(fallbackPreset),
        granularity: getGranularityForPreset(fallbackPreset),
    });
    try {
        const raw = localStorage.getItem(REPORTING_TIMEFRAME_STORAGE_KEY);
        if (!raw) return fallback();
        const parsed = JSON.parse(raw) as Partial<ReportingTimeframeApplyPayload>;
        if (!isDatePreset(parsed.preset)) return fallback();
        const granularity = isGranularity(parsed.granularity)
            ? parsed.granularity
            : getGranularityForPreset(parsed.preset);
        if (parsed.preset === 'custom') {
            if (typeof parsed.startDate !== 'string' || !YMD.test(parsed.startDate)) return fallback();
            if (typeof parsed.endDate !== 'string' || !YMD.test(parsed.endDate)) return fallback();
            return {
                preset: 'custom',
                startDate: parsed.startDate,
                endDate: parsed.endDate,
                granularity,
            };
        }
        return {
            preset: parsed.preset,
            ...getPresetDates(parsed.preset),
            granularity,
        };
    } catch {
        return fallback();
    }
}

export function saveReportingTimeframe(timeframe: ReportingTimeframeApplyPayload): void {
    try {
        localStorage.setItem(REPORTING_TIMEFRAME_STORAGE_KEY, JSON.stringify(timeframe));
    } catch {
        // Ignore blocked storage.
    }
}
